import { useLayoutEffect, useRef, useState } from "react";
import { useMedia } from "react-use";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { UPGRADE_LOCATIONS } from "@/lib/tracking";
import useUpgradeCta from "src/hooks/useUpgradeCta";

/**
 * The unlock banner over a locked grid. By default it covers the grid's lower part; given `top` (px from
 * its positioned parent's top, the first scrambled row of an answer with free rows), it covers only the
 * rows from there down, so the free rows above it stay readable.
 */
const ObfuscationBanner = ({ theme, top }: { theme: "dark" | "light"; top?: number }) => {
    // The same way in as every other locked surface: the first-month offer by price for a
    // visitor or a free account, the plain upgrade otherwise.
    const { label, caption, loading: appUpgradeLoading, onClick: handleUpgradeClick } = useUpgradeCta(UPGRADE_LOCATIONS.OBFUSCATION_BANNER);

    const [containerHeight, setContainerHeight] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const isSmall = containerHeight <= 180;
    const isMobile = useMedia("(max-width: 768px)");

    // Measured before the first paint: the banner sits on the grid's bottom edge, so starting at the
    // 40% guess and correcting to 60% a frame later grew it upwards over the rows (a layout shift
    // on every locked grid). It only renders on the client, after the grid has mounted.
    useLayoutEffect(() => {
        if (!containerRef.current) return;
        setContainerHeight(containerRef.current.getBoundingClientRect().height);

        const observer = new ResizeObserver(([entry]) => {
            setContainerHeight(entry.contentRect.height);
        });

        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, []);

    return (
        <Box
            ref={containerRef}
            sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                ...(top === undefined ? { height: isSmall ? "40%" : "60%" } : { top }),
                background: theme === "dark" ? "linear-gradient(to bottom, rgba(18,18,18,0), rgba(18,18,18,0.95) 20%, rgba(18,18,18,0.98))" : "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.95) 20%, rgba(255,255,255,0.98))",
                display: "flex",
                alignItems: "center",
                justifyContent: isSmall ? "flex-end" : "center",
                flexDirection: "column",
                color: theme === "dark" ? "#fff" : "#000",
                zIndex: 3,
                gap: isSmall ? 1 : 3,
                my: isSmall ? 1 : 0,
            }}
        >
            <Typography variant="h1" component="h2" sx={{ fontSize: isSmall ? "1.2rem !important" : isMobile ? "1.5rem !important" : "2rem !important", fontWeight: "bold", mb: isSmall ? 0 : 1 }}>
                Unlock complete data
            </Typography>
            <Typography variant="body1">{caption || "Access advanced analytics, comprehensive datasets, and premium insights."}</Typography>
            <Button variant="contained" size={isSmall ? "small" : "large"} disabled={appUpgradeLoading} onClick={() => handleUpgradeClick()} sx={{ color: "white", textTransform: "none" }}>
                {appUpgradeLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : label}
            </Button>
        </Box>
    );
};

export default ObfuscationBanner;
